import { Box, Flex, HStack, Icon, IconButton, Text } from '@chakra-ui/react'
import { ConditionalValue } from '@chakra-ui/react'
import { Collapsible } from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneStatus, TrackedParkingLot } from '~/atoms/WebphoneState'
import { QuickActionButtonSizes } from '~/components/shared/QuickActionButton'
import { AudioSettings } from '~/pages/WebPhone/AudioSettings'
import {
  CallControlButton,
  ControlButtonTypes,
} from '~/pages/WebPhone/CallControlButton'
import { Keypad } from '~/pages/WebPhone/KeyPad'
import { ParkControl } from '~/pages/WebPhone/ParkControl'
import { TransferControl } from '~/pages/WebPhone/TransferControl'
import { Size } from '~/types/ThemeTypes'

import { ExtensionPreview } from '@mangovoice/types'

import { useCallControlStyle } from './componentStyles/CallControl.style'
import { isFocusLost } from './utils/keyboardUtils'

const DRAWER_CLOSE_BUTTON_ATTRIBUTE = 'data-drawer-close'
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

const focusFirstDrawerField = (drawer: HTMLElement | null) => {
  if (!drawer) return
  const focusable = Array.from(
    drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  )
  const firstField = focusable.find(
    (element) => !element.hasAttribute(DRAWER_CLOSE_BUTTON_ATTRIBUTE),
  )
  ;(firstField ?? focusable[0])?.focus()
}

interface ControlDrawerHeaderProps {
  onCancelClick: () => void
  title?: string
}
const ControlDrawerHeader = ({
  onCancelClick: handleCancelClick,
  title,
}: ControlDrawerHeaderProps) => {
  const { t } = useTranslation()
  const style = useCallControlStyle()

  return (
    <Flex css={style.drawerHeader}>
      {title ? (
        <Text css={style.drawerHeaderText}>{title}</Text>
      ) : (
        <Text css={style.drawerHeaderText} aria-hidden="true">
          {'\u200E'}
        </Text>
      )}
      <IconButton
        {...{ [DRAWER_CLOSE_BUTTON_ATTRIBUTE]: '' }}
        css={style.drawerHeaderIcon}
        aria-label={t('controlDrawer.ariaLabel')}
        onClick={handleCancelClick}
      >
        <Icon css={style.drawerHeaderIcon} aria-hidden="true">
          <ChevronDown />
        </Icon>
      </IconButton>
    </Flex>
  )
}

type SelectedControlState = 'KEYPAD' | 'AUDIO' | 'PARK' | 'TRANSFER'

interface CallControlProps {
  callStatus: PhoneStatus
  size: ConditionalValue<QuickActionButtonSizes>
  isBelowMaxCallLimit: boolean
  isInConference: boolean
  isRinging: boolean
  isMuted: boolean
  onMuteClick: () => void
  isOnHold: boolean
  onHoldClick: () => void
  onParkCall: (parkExtension: string) => void
  onTransferCall: (target: string) => void
  onDialButtonClick: (digit: string) => void
  audioInputs: MediaDeviceInfo[]
  selectedInput: string
  onInputDeviceChange: (deviceId: string) => void
  audioOutputs: MediaDeviceInfo[]
  selectedOutput: string
  onOutputDeviceChange: (deviceId: string) => void
  pbxExtensions: ExtensionPreview[]
  parkingLots: TrackedParkingLot[]
  onCallStop: () => void
  isControlDrawerOpen: boolean
  onMergeCalls: () => void
  onSplitConference: () => void
  onNewCall: () => void
  toggleDrawer: (isActive: boolean, overrideIsOpen?: boolean) => void
}
export const CallControl = ({
  callStatus,
  size,
  isBelowMaxCallLimit,
  isInConference,
  isRinging,
  isMuted,
  onMuteClick,
  isOnHold,
  onHoldClick,
  onParkCall,
  onTransferCall,
  onDialButtonClick,
  audioInputs,
  selectedInput,
  onInputDeviceChange,
  audioOutputs,
  selectedOutput,
  onOutputDeviceChange,
  pbxExtensions,
  parkingLots,
  isControlDrawerOpen,
  onCallStop,
  onMergeCalls,
  onSplitConference,
  onNewCall,
  toggleDrawer,
}: CallControlProps) => {
  const { t } = useTranslation()
  const styles = useCallControlStyle({ size })
  const [openControlState, setOpenControlState] =
    useState<SelectedControlState>('KEYPAD')

  const barRef = useRef<HTMLDivElement>(null)
  const drawerId = useId()
  const drawerRef = useRef<HTMLDivElement>(null)
  const drawerTogglesRef = useRef<
    Partial<Record<SelectedControlState, HTMLButtonElement | null>>
  >({})
  const setDrawerToggleRef =
    (control: SelectedControlState) => (element: HTMLButtonElement | null) => {
      drawerTogglesRef.current[control] = element
    }

  const openDrawer = isControlDrawerOpen ? openControlState : null
  const previousOpenDrawerRef = useRef<SelectedControlState | null>(null)

  useEffect(() => {
    const previousOpenDrawer = previousOpenDrawerRef.current
    previousOpenDrawerRef.current = openDrawer
    if (openDrawer === previousOpenDrawer) return

    if (openDrawer) {
      const frame = requestAnimationFrame(() =>
        focusFirstDrawerField(drawerRef.current),
      )
      return () => cancelAnimationFrame(frame)
    }

    if (!previousOpenDrawer) return
    const active = document.activeElement
    if (isFocusLost(barRef.current) || drawerRef.current?.contains(active)) {
      drawerTogglesRef.current[previousOpenDrawer]?.focus()
    }
  }, [openDrawer])

  const isKeyPadActive = openControlState === 'KEYPAD'
  const isAudioSettingsActive = openControlState === 'AUDIO'
  const isParkControlActive = openControlState === 'PARK'
  const isTransferControlActive = openControlState === 'TRANSFER'

  const handleKeyPadControlClick = () => {
    toggleDrawer(isKeyPadActive)
    setOpenControlState('KEYPAD')
  }
  const handleAudioSettingsClick = () => {
    toggleDrawer(isAudioSettingsActive)
    setOpenControlState('AUDIO')
  }
  const handleParkControlClick = () => {
    toggleDrawer(isParkControlActive)
    setOpenControlState('PARK')
  }
  const handleTransferControlClick = () => {
    toggleDrawer(isTransferControlActive)
    setOpenControlState('TRANSFER')
  }
  const handleCancelClick = () => {
    toggleDrawer(false, false)
  }
  const handleMergeCallsClick = () => {
    onMergeCalls()
  }
  const handleSplitConferenceClick = () => {
    onSplitConference()
  }
  const handleNewCallClick = () => {
    onNewCall()
  }

  const conferenceButton = useMemo(() => {
    if (isInConference) {
      return (
        <CallControlButton
          type={ControlButtonTypes.SPLIT}
          size={size}
          onClick={handleSplitConferenceClick}
        />
      )
    }
    if (isBelowMaxCallLimit) {
      return (
        <CallControlButton
          type={ControlButtonTypes.NEW}
          size={size}
          onClick={handleNewCallClick}
        />
      )
    }
    return (
      <CallControlButton
        type={ControlButtonTypes.MERGE}
        size={size}
        onClick={handleMergeCallsClick}
      />
    )
  }, [isInConference, isBelowMaxCallLimit, size])

  const getEstablishedExclusiveState = (
    controlType: SelectedControlState | 'HOLD',
    disabled?: boolean,
    active?: boolean,
  ) => {
    if (isRinging || disabled) {
      return { isActive: false, isDisabled: true }
    }
    if ((openControlState === controlType && isControlDrawerOpen) || active) {
      return { isActive: true, isDisabled: false }
    }
    return { isActive: false, isDisabled: false }
  }

  const keypadState = {
    isActive: isKeyPadActive && isControlDrawerOpen,
    isDisabled: false,
  }
  const audioSettingsState = {
    isActive: isAudioSettingsActive && isControlDrawerOpen,
    isDisabled: false,
  }
  const parkControlState = getEstablishedExclusiveState('PARK', isInConference)
  const transferControlState = getEstablishedExclusiveState(
    'TRANSFER',
    isInConference,
  )
  const isDrawerOpenFor = (controlType: SelectedControlState) =>
    isControlDrawerOpen && openControlState === controlType
  const muteState = { isActive: isMuted, isDisabled: false }
  const holdState = getEstablishedExclusiveState('HOLD', undefined, isOnHold)

  const barHeight = barRef.current?.offsetHeight ?? 0

  return (
    <>
      {/* Position and zIndex allow the drawer to display over other elements
       without disrupting flow */}
      <HStack
        ref={barRef}
        gap={styles.bar?.gap}
        css={styles.bar}
        position="relative"
      >
        <Flex
          position="absolute"
          bottom={barHeight}
          left="50%"
          transform="translate(-50%, 4px)"
          direction="column"
          alignItems="center"
          justifyContent="flex-end"
          zIndex="2"
        >
          <Collapsible.Root open={isControlDrawerOpen}>
            <Collapsible.Content>
              <Box ref={drawerRef} id={drawerId} css={styles.drawer} zIndex="2">
                {isKeyPadActive && isControlDrawerOpen && (
                  <>
                    <ControlDrawerHeader onCancelClick={handleCancelClick} />
                    <Flex paddingBottom="28px">
                      <Keypad
                        size={{ base: Size.sm, xl: Size.md }}
                        onButtonClick={onDialButtonClick}
                      />
                    </Flex>
                  </>
                )}
                {isAudioSettingsActive && (
                  <>
                    <ControlDrawerHeader
                      title={t('callControl.audioSettings.title')}
                      onCancelClick={handleCancelClick}
                    />
                    <AudioSettings
                      inputs={audioInputs}
                      selectedInput={selectedInput}
                      onInputDeviceChange={onInputDeviceChange}
                      outputs={audioOutputs}
                      selectedOutput={selectedOutput}
                      onOutputDeviceChange={onOutputDeviceChange}
                    />
                  </>
                )}
                {isParkControlActive && (
                  <>
                    <ControlDrawerHeader
                      title={t('callControl.parkCall')}
                      onCancelClick={handleCancelClick}
                    />
                    <ParkControl
                      parkingLots={parkingLots}
                      onParkCall={onParkCall}
                    />
                  </>
                )}
                {isTransferControlActive && (
                  <>
                    <ControlDrawerHeader
                      title={t('callControl.transferCall')}
                      onCancelClick={handleCancelClick}
                    />
                    <TransferControl
                      onTransfer={onTransferCall}
                      extensions={pbxExtensions}
                    />
                  </>
                )}
              </Box>
            </Collapsible.Content>
          </Collapsible.Root>
        </Flex>

        <CallControlButton
          type={ControlButtonTypes.MUTE}
          size={size}
          onClick={onMuteClick}
          isActive={muteState.isActive}
          disabled={muteState.isDisabled}
        />
        <CallControlButton
          type={ControlButtonTypes.HOLD}
          size={size}
          onClick={onHoldClick}
          isActive={holdState.isActive}
          disabled={holdState.isDisabled}
        />
        <CallControlButton
          ref={setDrawerToggleRef('KEYPAD')}
          type={ControlButtonTypes.KEYPAD}
          size={size}
          onClick={handleKeyPadControlClick}
          isActive={keypadState.isActive}
          disabled={keypadState.isDisabled}
          ariaExpanded={isDrawerOpenFor('KEYPAD')}
          ariaControls={drawerId}
        />
        <CallControlButton
          ref={setDrawerToggleRef('AUDIO')}
          type={ControlButtonTypes.AUDIO}
          size={size}
          onClick={handleAudioSettingsClick}
          isActive={audioSettingsState.isActive}
          disabled={audioSettingsState.isDisabled}
          ariaExpanded={isDrawerOpenFor('AUDIO')}
          ariaControls={drawerId}
        />
        <CallControlButton
          ref={setDrawerToggleRef('PARK')}
          type={ControlButtonTypes.PARK}
          size={size}
          onClick={handleParkControlClick}
          isActive={parkControlState.isActive}
          disabled={parkControlState.isDisabled}
          ariaExpanded={isDrawerOpenFor('PARK')}
          ariaControls={drawerId}
        />
        <CallControlButton
          ref={setDrawerToggleRef('TRANSFER')}
          type={ControlButtonTypes.TRANSFER}
          size={size}
          onClick={handleTransferControlClick}
          isActive={transferControlState.isActive}
          disabled={transferControlState.isDisabled}
          ariaExpanded={isDrawerOpenFor('TRANSFER')}
          ariaControls={drawerId}
        />
        {conferenceButton}
        <CallControlButton
          type={ControlButtonTypes.END}
          size={size}
          onClick={onCallStop}
          disabled={
            callStatus === PhoneStatus.TRANSPORT_DISCONNECTED ||
            callStatus === PhoneStatus.ERROR ||
            callStatus === PhoneStatus.MIC_DENIED
          }
        />
      </HStack>
    </>
  )
}
